import React, { useState } from 'react';
import { useQuery } from 'react-query';
import Select from 'react-select';
import { AssetType, assetTypesApi } from '../../../../utils/api/asset';
import { assetBulkUploadApi } from '../../../../utils/api/bulkUpload';
import BulkUploadModal from '../../../Shared/BulkUpload/BulkUploadModal';

// Matches the app's other react-select controls (e.g. CorrectiveActionsTab).
const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 42,
    borderColor: state.isFocused ? '#48af98' : '#b1b8b6',
    borderRadius: 6,
    boxShadow: 'none',
    '&:hover': { borderColor: '#48af98' },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base: any, state: any) => ({
    ...base,
    backgroundColor: state.isSelected
      ? 'rgba(72, 175, 152, 0.15)'
      : state.isFocused
        ? 'rgba(72, 175, 152, 0.08)'
        : 'white',
    color: 'inherit',
  }),
  menuPortal: (base: any) => ({ ...base, zIndex: 2000 }),
};

// Code pill in the type's own colour, then its name.
const typeLabel = (type: AssetType) => (
  <span className='bulk-upload__type'>
    <span
      className='bulk-upload__type-code'
      style={{
        backgroundColor: type.featureable_data?.config?.color || '#6B7280',
      }}
    >
      {type.code}
    </span>
    {type.name}
  </span>
);

interface IAssetBulkUploadProps {
  projectId: number;
  divisionId: number;
  handleClose: () => void;
  onSaved: () => void;
}

/**
 * Add Bulk Asset. The template has one column per field of the
 * chosen asset type, so the type is picked before downloading.
 */
const AssetBulkUpload: React.FC<IAssetBulkUploadProps> = ({
  projectId,
  divisionId,
  handleClose,
  onSaved,
}) => {
  const [typeId, setTypeId] = useState<number | null>(null);
  const typesQuery = useQuery(['assetTypes', divisionId], () =>
    assetTypesApi.index(divisionId),
  );
  const type = typesQuery.data?.find((t) => t.id === typeId);

  return (
    <BulkUploadModal
      entity='Asset'
      api={typeId ? assetBulkUploadApi(projectId, typeId) : null}
      handleClose={handleClose}
      onCreated={onSaved}
      fileLabel={type?.code}
      tooltip='Asset type fields, statuses and forms change, so it is recommended to use latest template'
      templateNote={
        <>
          <p>Pick the asset type; its fields become columns.</p>
          <label className='bulk-upload__label' htmlFor='bulk-asset-type'>
            Asset type *
          </label>
          <div className='bulk-upload__select'>
            <Select<AssetType>
              inputId='bulk-asset-type'
              options={typesQuery.data || []}
              value={type || null}
              onChange={(selected) => setTypeId(selected?.id ?? null)}
              getOptionValue={(t) => String(t.id)}
              getOptionLabel={(t) => `${t.code} ${t.name}`}
              formatOptionLabel={typeLabel}
              isLoading={typesQuery.isLoading}
              placeholder='Search or select an asset type'
              styles={selectStyles}
              menuPortalTarget={document.body}
            />
          </div>
        </>
      }
    />
  );
};

export default AssetBulkUpload;
